import { Box, NoSsr, Typography, Tooltip, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FallbackTypography from 'src/components/shared/FallbackTypography';
import SingleValueEditBox from './SingleValueEditBox';
import { useMsal } from '@azure/msal-react';
import { setAllItemsStatus, setStatus, updateSupplierStatus } from 'src/store/reservations/slices/ReservationsManagerSlice';
import { StatusList, ReservationStatus } from 'src/utils/common';
import dayjs from 'dayjs';
import { dateFormat } from 'src/utils/dateFormat';
import { ContentCopy } from '@mui/icons-material';

const ViewReservationsInfo = ({ setError }) => {
    const dispatch = useDispatch();
    const { reservationOverview } = useSelector((state) => state.reservationsSummaryReducer);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const { instance, accounts } = useMsal();
    const [localError, setLocalError] = useState(null);
    const [copied, setCopied] = useState(false);

    const handleStatusChange = (value) => {
        setSuccess(false);
        setLocalError(null);
        dispatch(setStatus({ status: value }));
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(reservationOverview?.ReservationReference || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const isAllConfirmed = reservationOverview?.Hotels?.every(h => h.Status === ReservationStatus.Confirmed) &&
        reservationOverview?.Services?.every(s => s.Status === ReservationStatus.Confirmed);

    const filteredStatusList = StatusList.map(option => ({
        ...option,
        disabled: option.value === ReservationStatus.Confirmed && isAllConfirmed
    }));

    const handleUpdateStatus = async () => {
        setLoading(true);
        setError(null);
        setLocalError(null);

        const requestPayload = {
            Status: reservationOverview?.Status,
        }

        try {
            await dispatch(
                updateSupplierStatus(
                    instance,
                    accounts,
                    reservationOverview.Id,
                    requestPayload
                )
            );
            if (reservationOverview?.Status === ReservationStatus.Confirmed) {
                dispatch(setAllItemsStatus({ status: ReservationStatus.Confirmed }));
            }

            setSuccess(true);
            setLocalError(null);
        } catch (error) {
            setError(error);
            setLocalError(error);
        } finally {
            setLoading(false);
            setSuccess(true);
        }
    }

    const getStatusValue = () => {
        
        if (reservationOverview?.Status === ReservationStatus.Cancelled) {
            return ReservationStatus.Cancelled;
        }

        if (isAllConfirmed && reservationOverview?.Status === ReservationStatus.Confirmed) {
            return ReservationStatus.Confirmed;
        }

        return reservationOverview?.Status;
    };

    const isStatusDisabled = () => {
        if (reservationOverview?.Status === ReservationStatus.Cancelled) {
            return true;
        }

        if (reservationOverview?.Status === ReservationStatus.Confirmed) {
            return true;
        }

        return false;
    };

    return (
        <Grid container
            sx={{
                mb: 1,
                borderRadius: 1,
                padding: 2,
            }}>
            {/* Left Section */}
            <Grid size={{ xs: 12, sm: 2.2 }} container direction="column" alignItems="flex-start">
                <Grid container spacing={1} alignItems="center" mb={2}>
                    <SingleValueEditBox
                        title="Status"
                        value={getStatusValue()}
                        onChange={handleStatusChange}
                        onSave={handleUpdateStatus}
                        loading={loading}
                        success={success}
                        error={localError}
                        type="select"
                        options={filteredStatusList}
                        disabled={isStatusDisabled()}
                        titleValueGap={1}
                    />
                </Grid>
            </Grid>
            {/* Left Section */}
            <Grid size={{ xs: 12, sm: 2.5 }} container direction="column" alignItems="flex-start">
                <Grid container spacing={1} alignItems="center" justifyContent="start" mb={1}>
                    <Grid item>
                        <Typography variant="h7" >Service Start Date:</Typography>
                    </Grid>
                    <Grid item>
                        <FallbackTypography variant="h7" fontWeight="bold"
                            value={reservationOverview.StartDate && dayjs(reservationOverview.StartDate, dateFormat).format(dateFormat)}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={1} alignItems="center" justifyContent="end" mb={1}>
                    <Grid item>
                        <Typography variant="h7" >Service End Date:</Typography>
                    </Grid>
                    <Grid item>
                        <FallbackTypography fontWeight="bold" variant="h7"
                            value={reservationOverview.EndDate && dayjs(reservationOverview.EndDate, dateFormat).format(dateFormat)}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {/* Center Section */}
            <Grid size={{ xs: 12, sm: 2.5 }} container direction="column" alignItems="flex-start">
                <Grid container spacing={1} alignItems="center" justifyContent="start" mb={1}>
                    <Grid item>
                        <Typography variant="h7" >User:</Typography>
                    </Grid>
                    <Grid item>
                        <Tooltip title={reservationOverview?.AgentName || ''}>
                            <span>
                                <FallbackTypography
                                    variant="body2"
                                    fontWeight="bold"
                                    value={
                                        reservationOverview?.AgentName?.length > 25
                                            ? `${reservationOverview.AgentName.slice(0, 25)}...`
                                            : reservationOverview?.AgentName
                                    }
                                />
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Grid container spacing={1} alignItems="center" justifyContent="start" mb={1}>
                    <Grid item>
                        <Typography variant="h7" >Agent:</Typography>
                    </Grid>
                    <Grid item>
                        <Tooltip title={reservationOverview?.AgentName || ''}>
                            <span>
                                <FallbackTypography
                                    variant="body2"
                                    fontWeight="bold"
                                    value={
                                        reservationOverview?.AgentName?.length > 25
                                            ? `${reservationOverview.AgentName.slice(0, 25)}...`
                                            : reservationOverview?.AgentName
                                    }
                                />
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Grid>
            {/* Right Section */}
            <Grid size={{ xs: 12, sm: 3 }} container direction="column" alignItems="flex-start">
                <Grid container spacing={1} alignItems="center" justifyContent="start" mb={1}>
                    <Grid item>
                        <Typography variant="h7">Total Booking Cost:</Typography>
                    </Grid>
                    <Grid item>
                        <FallbackTypography variant="h7" fontWeight="bold"
                            value={`${reservationOverview?.Cost?.currency} ${reservationOverview?.Cost?.amount}`}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={1} alignItems="center" justifyContent="start" mb={1}>
                    <Grid item>
                        <Typography variant="h7">Passenger Details:</Typography>
                    </Grid>
                    <Grid item>
                        <FallbackTypography variant="h7" fontWeight="bold"
                            value={`${reservationOverview?.PaxCount} Pax`}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid size={{ xs: 12, sm: 1.8 }} container direction="column" alignItems="flex-end">
                <Grid container spacing={1} alignItems="center" justifyContent="flex-end" mb={1}>
                    <NoSsr>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                                '&:hover .copy-icon': {
                                    visibility: 'visible',
                                    opacity: 1,
                                },
                            }}>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: 'transparent',
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}>
                                <FallbackTypography
                                    value={reservationOverview?.ReservationReference}
                                    component="span"
                                />
                            </Box>

                            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                                <IconButton
                                    onClick={handleCopy}
                                    size="small"
                                    className="copy-icon"
                                    sx={{
                                        ml: 1,
                                        color: 'primary.main',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.3s',
                                        height: '100%',
                                    }}>
                                    <ContentCopy fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </NoSsr>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default ViewReservationsInfo;
